import { FormModel, Result } from 'src_common/common/mobx-utils/Form2/FormModel';
import {
    SignUpWelcomeState,
    WelcomeStepFormType,
} from 'src/domains/players/webview/components/SignUp/signupPrimary/signUpViews/createAccount/signupWelcome/SignupWelcome.state';
import {
    GenderFormModelType,
    YourDetailsFormType,
    YourDetailsState,
} from 'src/domains/players/webview/components/SignUp/signupPrimary/signUpViews/createAccount/yourDetails/YourDetails.state';
import { action, observable, makeObservable, runInAction } from 'mobx';
import { getCookie, setCookie } from 'src/domains/layouts/config/config';
import { SignUpParamsType } from 'src/domains/players/webview/components/SignUp/signupPrimary/signupState/SignUpParams';
import { isCountryCodeType } from 'src/domains/layouts/config/countries';
import { DateTime } from 'src_common/utils/time/time';
import { createAccountPrimary } from 'src_server/trpc/types';
import { CurrencyType, isCurrencyCode } from 'src_common/common/amount/website-money/currency';
import { GoogleTagManagerState } from 'src/domains/layouts/state/googleState/GoogleTagManagerState';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { StarRouter } from 'src/domains/layouts/state/router/StarRouter';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';

interface SignUpStepsGroupModelType {
    signupWelcomeState: WelcomeStepFormType;
    yourDetailsState: YourDetailsFormType;
    titleState: GenderFormModelType;
}

interface CallbacksType {
    readonly redirectToNextStep: () => void;
    readonly setLoading: (isLoading: boolean) => void;
    readonly resetPromoCodeInLocalStorage: () => void;
}

export type CreateAccountStageType = 'welcome' | 'your-details';

export class CreateAccountStep {
    @observable public backendCreateAccountError: string | null = null;
    @observable public currentStage: CreateAccountStageType;
    @observable public userCreationToken: string | null = null;

    public welcomeState: SignUpWelcomeState;
    public yourDetailsState: YourDetailsState;

    @observable.ref public signUpFormModel: FormModel<createAccountPrimary.CreateAccountInputType>;
    @observable.ref private signUpStepsGroup: FormModel<SignUpStepsGroupModelType>;

    public constructor(
        public readonly params: SignUpParamsType,
        private readonly callbacks: CallbacksType,
        private stage?: CreateAccountStageType,
        private readonly initialUserData?: WelcomeStepFormType
    ) {
        makeObservable(this);
        const config = ConfigComponents.get(this.params.common);
        this.currentStage = this.stage === undefined ? 'welcome' : this.stage;

        this.welcomeState = new SignUpWelcomeState(
            this.params.customKeyboard,
            this.moveToNextStage,
            params.common,
            this.initialUserData
        );
        this.yourDetailsState = new YourDetailsState(this.params.customKeyboard, config, this.params.common);

        this.signUpStepsGroup = FormModel.group({
            signupWelcomeState: this.welcomeState.welcomeStepFormModel,
            yourDetailsState: this.yourDetailsState.yourDetailsFormModel,
            titleState: this.yourDetailsState.genderTitleForModel,
        });

        this.signUpFormModel = this.signUpStepsGroup.map(
            (value): Result<createAccountPrimary.CreateAccountInputType> => {
                const btag = getCookie('btag');
                const stag = getCookie('stag');
                const cxd = getCookie('cxd');
                const referrer = getCookie('referrer');

                const countryKey = this.yourDetailsState.country.currentValueFormatted?.key ?? '';
                const mobilePhoneCountryKey = this.yourDetailsState.phoneNumber.prefix.currentValueFormatted?.key ?? '';
                const currency = value.yourDetailsState.currency;
                const configCurrencyDefault = config.config.currencyDefault;
                const defCurrency: CurrencyType = isCurrencyCode(configCurrencyDefault) ? configCurrencyDefault : 'GBP';

                const createAccountData: createAccountPrimary.CreateAccountInputType = {
                    email: value.signupWelcomeState.email,
                    password: value.signupWelcomeState.password,
                    title: value.titleState.genderTitle,
                    firstName: value.yourDetailsState.firstName,
                    surname: value.yourDetailsState.lastName,
                    birthDate: value.yourDetailsState.dateOfBirth.format('YYYY-MM-DD'),
                    currency: isCurrencyCode(currency) ? currency : defCurrency,
                    country: isCountryCodeType(countryKey) ? countryKey : 'GB',
                    contactPreferences: [],
                    postCode: value.yourDetailsState.postcode,
                    addressLine1: value.yourDetailsState.addressLine1,
                    addressLine2: config.config.signUpSecondAddressLine
                        ? value.yourDetailsState.addressLine2
                        : undefined,

                    city: value.yourDetailsState.city,
                    mobilePhone: {
                        country: isCountryCodeType(mobilePhoneCountryKey) ? mobilePhoneCountryKey : 'GB',
                        prefix: value.yourDetailsState.prefix.replace('+', ''),
                        number: value.yourDetailsState.phoneNumber,
                    },
                    referrer: referrer ?? undefined,
                    incomeaccess: value.signupWelcomeState.affiliateId ?? btag ?? stag ?? cxd ?? undefined,
                    promoID: value.signupWelcomeState.promoCode,
                };

                if (value.signupWelcomeState.affiliateId !== undefined) {
                    setCookie('btag', value.signupWelcomeState.affiliateId, 31, 'None');
                }

                return Result.createOk(createAccountData);
            }
        );
    }

    public static newFromParams(
        params: SignUpParamsType,
        callbacks: CallbacksType,
        stage?: CreateAccountStageType,
        initialUserData?: WelcomeStepFormType
    ): CreateAccountStep {
        return new CreateAccountStep(params, callbacks, stage, initialUserData);
    }

    @action private onCreateAccount = async (): Promise<boolean> => {
        this.signUpStepsGroup.setAsVisited();
        const fullFormResult = this.signUpFormModel.result;
        const language = LanguagesState.get(this.params.common);

        if (fullFormResult.value.type === 'ok') {
            this.yourDetailsState.loadingCreateAccountReq = true;

            if (this.welcomeState.promoCodeTextState.result.value.type === 'error') {
                const message = language.getTranslation(
                    'sign-up.create-account.promo-code.error',
                    'This promo code is not valid'
                );
                this.backendCreateAccountError = message;
                this.yourDetailsState.loadingCreateAccountReq = false;
                return false;
            }

            const createAccountResponse = await this.params.common.trpcClient.client.signup.createAccountDefault.mutate(
                fullFormResult.value.data
            );

            runInAction(() => {
                this.yourDetailsState.loadingCreateAccountReq = false;
            });

            if (createAccountResponse.responseStatus === 'error_input') {
                const arrEl = createAccountResponse.data.errors[0];

                if (arrEl?.field !== undefined && arrEl.field !== null) {
                    if (arrEl.debugDetails !== undefined && arrEl.debugDetails !== null) {
                        const field = arrEl.field;
                        const debugDetails = arrEl.debugDetails;
                        runInAction(() => {
                            this.backendCreateAccountError = `In ${field} - ${debugDetails}`;
                        });
                    }
                }
                console.error('onCreateAccount - error1', createAccountResponse.data);
                return false;
            } else if (createAccountResponse.responseStatus === 'error_api') {
                const errorCode = createAccountResponse.data.code;
                if (errorCode === 'single_license_constraint') {
                    runInAction(() => {
                        this.backendCreateAccountError = language.getTranslation(
                            'sign-up.create-account.error-single-license-constraint',
                            'Thank you for attempting to open an account with us. Having reviewed your details, we regret to inform you that you have already opened an account on our operating license and will not be able to open a further account at this time.'
                        );
                    });
                } else if (errorCode === 'cross_accounts_unavailable') {
                    runInAction(() => {
                        this.backendCreateAccountError = language.getTranslation(
                            'sign-up.create-account.internal-server',
                            'Sorry, we seem to have a problem. Please try again later.'
                        );
                    });
                }
                return false;
            }
            //TODO: try to move AV to ProfileVerification.state. NEW INTEGRATION
            //if (createAccountResponse.responseStatus === 'success') {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (createAccountResponse.responseStatus === 'success') {
                StarRouter.get(this.params.common).clearTrackingTags();

                setCookie('btag', '', 0, 'None');
                setCookie('stag', '', 0, 'None');
                setCookie('cxd', '', 0, 'None');
            }

            this.params.common.session.setEphemeralAccess(createAccountResponse.data.ephemeralToken ?? null);
            this.setUserCreationToken(createAccountResponse.data.token);
            this.callbacks.setLoading(true);
            this.callbacks.resetPromoCodeInLocalStorage();

            return true;
        }

        return false;
    };

    @action private setUserCreationToken = (creationToken: string | null): void => {
        this.userCreationToken = creationToken;
    };

    @action public createAccountAndMoveToNextStep = async (): Promise<void> => {
        // shortcut to omit creating account ang move to responsible gambling
        // 1. go to http://localhost:3030/?account=signup
        // 2. uncomment
        // this.callbacks.redirectToNextStep();
        // return;
        // 3. signup with email but without user details - click Agree & Join
        const config = ConfigComponents.get(this.params.common).config;
        if (config.zipCodeAndCountryCheck === true) {
            this.yourDetailsState.validateOnSubmit = true;
        }

        this.yourDetailsState.yourDetailsFormModel.setAsVisited();
        this.yourDetailsState.genderTitleForModel.setAsVisited();

        if (
            this.yourDetailsState.genderTitleForModel.result.value.type === 'error' &&
            config.signUpCheckboxTC === false
        ) {
            this.yourDetailsState.showFullAddressFormHandler();
            return;
        }

        const createAccountStatus = await this.onCreateAccount();
        if (createAccountStatus) {
            this.callbacks.setLoading(false);
            this.callbacks.redirectToNextStep();
        } else {
            this.callbacks.setLoading(false);
        }
    };

    @action public moveToNextStage = (): void => {
        if (this.currentStage === 'welcome') {
            const date = DateTime.current().utc().format('YYYY-MM-DD HH:mm:ss [GMT]');

            const googleTagManager = GoogleTagManagerState.get(this.params.common);
            googleTagManager.gtmSignUpStepOne(this.welcomeState.promoCodeTextState.value, date);
            this.currentStage = 'your-details';
        } else {
            this.currentStage = 'welcome';
        }
    };
}
